import { FC, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Typography, message } from 'antd';
import {
  WorkspaceResponse,
  useGetWorkspaces,
  useLeaveWorkspace,
} from '@/shared/api';
import { useRouterContext, useRouterPrivateContext } from '@/shared/router';
import { Col, Row } from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';

type Props = {
  open: boolean;
  onCancel: () => void;
};

const { Title } = Typography;

export const LeaveWorkspaceModal: FC<Props> = ({ open, onCancel }) => {
  const queryClient = useQueryClient();
  const { activeOrganization } = useRouterContext();
  const { selectedWorkspace, setSelectedWorkspace } = useRouterPrivateContext();

  const { data: workspacesData } = useGetWorkspaces(
    {},
    { select: ({ data }) => data },
  );

  const { mutate: leaveWorkspace, isLoading: leaveWorkspaceLoading } =
    useLeaveWorkspace();

  const handleWorkspaceLeave = () => {
    try {
      leaveWorkspace(
        {
          pathParams: { workspaceId: selectedWorkspace?.id || '' },
        },
        {
          onSuccess() {
            if (isMemberOfOneWorkspace) {
              message.success('You have left the organization.');
              return location.reload();
            }

            queryClient.invalidateQueries([
              'workspaces',
              'organizations',
              activeOrganization?.id,
              'my',
            ]);
            setSelectedWorkspace(
              workspacesData?.find(
                ({ id }: WorkspaceResponse) => id !== selectedWorkspace?.id,
              ),
            );
            onCancel();
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'leave_workspace_modal',
        action: 'leave_workspace',
      });
    }
  };

  const isMemberOfOneWorkspace = useMemo(
    () => workspacesData?.length === 1,
    [workspacesData?.length],
  );

  return (
    <Modal
      title={
        <Col
          alignItems="flex-start"
          style={{ margin: '10px 0', width: '100%' }}
        >
          <Title level={4} style={{ lineHeight: 1, marginTop: 15 }}>
            {isMemberOfOneWorkspace
              ? 'Leave workspace and organization'
              : 'Leave workspace'}
          </Title>
        </Col>
      }
      destroyOnClose
      width={480}
      open={open}
      footer={null}
      style={{ maxWidth: '90%' }}
      onCancel={onCancel}
    >
      <div>
        By leaving <strong>{selectedWorkspace?.title}</strong> workspace you
        will no longer have access to its procs nor instances.
      </div>
      <br />
      {isMemberOfOneWorkspace ? (
        <>
          <div>
            As this workspace is the only one you are a member of, you will be
            also removed from <strong>{activeOrganization?.name}</strong>{' '}
            organization.
          </div>
          <br />
          <div>
            Are you sure you want to leave this worskapce and organization?
          </div>
        </>
      ) : (
        <div>Are you sure you want to leave this worskapce?</div>
      )}
      <br />
      <Row gap={15} style={{ flexWrap: 'wrap', marginTop: 30 }}>
        <Button onClick={onCancel} style={{ height: 40, flex: 2 }}>
          Cancel
        </Button>
        <Button
          danger
          type="primary"
          loading={leaveWorkspaceLoading}
          onClick={handleWorkspaceLeave}
          style={{ height: 40, flex: 3 }}
        >
          {isMemberOfOneWorkspace
            ? 'Leave workspace and organization'
            : 'Leave workspace'}
        </Button>
      </Row>
    </Modal>
  );
};
